import { api, type Lds1PurchaseRequestItem, queryKeys } from '@alpha/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatUsd, statusTone } from '../../lib/format';
import { Alert, Button, Card, Chip, FormGroup, Input } from '../../ui';
import { WALLET_TABS } from './Deposit';

const col = createColumnHelper<Lds1PurchaseRequestItem>();

/**
 * 2026-09-11, LDS1 subdomain only: client's explicit requirement, verbatim —
 * "USER JINKA pahle se ID ACTIVE HO CHUKA HAIN wo APNE ID SE 100$ KA LDS1
 * TOKAN Buy KAR SAKTA... aur jinko bhi new ID lagani hai unke wallet me
 * transfer kar denge" — an already-active member sends USDT to the company's
 * deposit address (shown below), and the platform automatically detects it
 * and sends the equivalent LDS1 to a wallet they choose — their own, or a
 * new member's, so that new member can activate with LDS1.
 */
export function BuyLds1() {
  const queryClient = useQueryClient();
  const { query, setPage } = useTableQuery();
  const [useOwnWallet, setUseOwnWallet] = useState(true);

  const info = useQuery({
    queryKey: ['lds1-purchase-info'],
    queryFn: () => api.lds1Purchase.info().then((r) => r.data),
  });

  const history = useQuery({
    queryKey: queryKeys.lds1Purchases(query),
    queryFn: () => api.lds1Purchase.list(query),
  });

  const form = useForm<{ usdt_amount: string; recipient_wallet_address: string }>({
    defaultValues: { usdt_amount: '', recipient_wallet_address: '' },
  });

  const submit = useMutation({
    mutationFn: (values: { usdt_amount: string; recipient_wallet_address: string }) =>
      api.lds1Purchase.create({
        usdt_amount: values.usdt_amount,
        recipient_wallet_address: useOwnWallet ? undefined : values.recipient_wallet_address,
      }),
    onSuccess: () => {
      form.reset();
      queryClient.invalidateQueries({ queryKey: ['lds1-purchases'] });
    },
  });

  const recheck = useMutation({
    mutationFn: (id: string) => api.lds1Purchase.recheck(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lds1-purchases'] }),
  });

  const columns = useMemo(
    () => [
      col.accessor('recipient_wallet_address', { header: 'Sent to' }),
      col.accessor('usdt_amount_expected', { header: 'USDT expected', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('usdt_amount_confirmed', {
        header: 'USDT confirmed',
        cell: (i) => (i.getValue() ? formatUsd(i.getValue()) : '—'),
      }),
      col.accessor('lds1_amount', { header: 'LDS1', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('status', {
        header: 'Status',
        cell: (i) => <Chip tone={statusTone(i.getValue())}>{i.getValue()}</Chip>,
      }),
      col.accessor('created_at', { header: 'Requested', cell: (i) => formatDateTime(i.getValue()) }),
      col.display({
        id: 'actions',
        header: 'Action',
        cell: (i) =>
          i.row.original.status === 'pending' ? (
            <Chip as="button" tone="gold" onClick={() => recheck.mutate(i.row.original.id)}>
              {recheck.isPending && recheck.variables === i.row.original.id ? 'Checking…' : 'Recheck'}
            </Chip>
          ) : null,
      }),
    ],
    [recheck],
  );

  return (
    <>
      <PageHeader
        title="Buy LDS1 with USDT"
        description="Send USDT to the company wallet — LDS1 is sent automatically once the payment is confirmed on-chain."
        tabs={WALLET_TABS}
      />

      <Card>
        <h3 className="card-title">Send USDT to</h3>
        {info.data ? (
          <>
            <p className="deposit-address">{info.data.deposit_address}</p>
            <p className="text-muted small">
              Network: {info.data.chain} · Rate: {formatUsd(info.data.lds_rate_usd)} per LDS1
            </p>
          </>
        ) : null}
        <p className="hint">
          Only an already-active member ID can use this. Send the exact USDT amount you plan to buy
          with, from your own registered wallet — the platform verifies the payment on-chain before
          sending any LDS1.
        </p>
      </Card>

      <Card>
        <h3 className="card-title">Request LDS1</h3>
        {submit.isSuccess ? (
          <Alert tone={submit.data.data.status === 'confirmed' ? 'success' : 'info'}>
            {submit.data.data.status === 'confirmed'
              ? `LDS1 sent. Tx: ${submit.data.data.request.send_tx_hash}`
              : submit.data.data.status === 'pending'
                ? 'USDT payment not found on-chain yet — it can take a few minutes. Use Recheck below once it confirms.'
                : `Request ${submit.data.data.status}.`}
          </Alert>
        ) : null}
        {submit.error ? <Alert tone="danger">{(submit.error as Error).message}</Alert> : null}

        <form onSubmit={form.handleSubmit((values) => submit.mutate(values))} noValidate>
          <FormGroup label="Amount (USDT you sent)">
            <Input type="number" step="0.01" min="1" placeholder="100" {...form.register('usdt_amount', { required: true })} />
          </FormGroup>

          <FormGroup label="Send the LDS1 to">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <input type="radio" checked={useOwnWallet} onChange={() => setUseOwnWallet(true)} />
              <span>My own registered wallet</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="radio" checked={!useOwnWallet} onChange={() => setUseOwnWallet(false)} />
              <span>A different wallet (e.g. a new member you're sponsoring)</span>
            </label>
          </FormGroup>

          {!useOwnWallet ? (
            <FormGroup label="Recipient wallet address">
              <Input
                placeholder="0x…"
                {...form.register('recipient_wallet_address', {
                  required: !useOwnWallet,
                  pattern: /^0x[a-fA-F0-9]{40}$/,
                })}
              />
            </FormGroup>
          ) : null}

          <Button type="submit" variant="gold" disabled={submit.isPending}>
            {submit.isPending ? 'Submitting…' : 'I have sent the USDT'}
          </Button>
        </form>
      </Card>

      <DataTable<Lds1PurchaseRequestItem>
        title="Purchase History"
        columns={columns}
        data={history.data?.data}
        meta={history.data?.meta}
        isLoading={history.isLoading}
        error={history.error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
